import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toolToast } from '../../utils/toolToast';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '../ui';
import { AppEmbedKey, EmbedKeyCreateRequest } from '../../types';
import { env } from '../../config/env';
import { embedKeyService } from '../../services';

interface EmbedKeyPanelProps {
  toolId: string;
}

const parseChannelFromToken = (token: string): string | undefined => {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) {
      return undefined;
    }
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = atob(padded);
    const payload = JSON.parse(decoded) as { channel?: string };
    const channel = String(payload.channel || '').trim();
    return channel || undefined;
  } catch {
    return undefined;
  }
};

const normalizeApiBase = (): string => {
  const envBase = String(env.REACT_APP_API_URL || '').trim();
  const fallback = typeof window !== 'undefined' ? window.location.origin : '';
  return (envBase || fallback).replace(/\/+$/, '');
};

export const EmbedKeyPanel: React.FC<EmbedKeyPanelProps> = ({ toolId }) => {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<AppEmbedKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [label, setLabel] = useState('default');
  const [allowSameOrigin, setAllowSameOrigin] = useState(false);
  const [maxRequestsPerHour, setMaxRequestsPerHour] = useState('');
  const [maxBandwidthMbPerDay, setMaxBandwidthMbPerDay] = useState('');
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [parentOrigin, setParentOrigin] = useState(
    typeof window !== 'undefined' ? window.location.origin : ''
  );
  const [expiresInSeconds, setExpiresInSeconds] = useState('900');
  const [snippet, setSnippet] = useState('');

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const rows = await embedKeyService.listKeys(toolId);
      setKeys(rows);
      if (!selectedKeyId && rows.length > 0) {
        setSelectedKeyId(rows[0].key_id);
      }
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: string }).message || '')
          : '';
      toolToast.error(message || t('tools.embedPanel.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [toolId, selectedKeyId, t]);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const activeKeyOptions = useMemo(
    () => keys.filter(item => item.is_active !== false),
    [keys]
  );

  const handleCreateKey = async (): Promise<void> => {
    setIsSaving(true);
    const payload: EmbedKeyCreateRequest = {
      label: String(label || '').trim() || 'default',
      allow_same_origin: allowSameOrigin,
    };
    const parsedMaxRequests = Number(maxRequestsPerHour);
    const parsedMaxBandwidth = Number(maxBandwidthMbPerDay);
    if (maxRequestsPerHour.trim() && Number.isFinite(parsedMaxRequests)) {
      payload.max_requests_per_hour = parsedMaxRequests;
    }
    if (maxBandwidthMbPerDay.trim() && Number.isFinite(parsedMaxBandwidth)) {
      payload.max_bandwidth_mb_per_day = parsedMaxBandwidth;
    }

    try {
      const created = await embedKeyService.createKey(toolId, payload);
      setKeys(prev => [created, ...prev]);
      setSelectedKeyId(created.key_id);
      toolToast.success(t('tools.embedPanel.createSuccess'));
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: string }).message || '')
          : '';
      toolToast.error(message || t('tools.embedPanel.createError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateSnippet = async (): Promise<void> => {
    setIsGenerating(true);
    setSnippet('');
    try {
      const response = await embedKeyService.issueToken(toolId, {
        key_id: selectedKeyId || undefined,
        parent_origin: String(parentOrigin || '').trim() || undefined,
        perms: ['read'],
        expires_in_seconds: Number(expiresInSeconds) || 900,
      });
      const token = response.token;
      const apiBase = normalizeApiBase();
      const iframeSrc = `${apiBase}/embed/apps/${toolId}?token=${encodeURIComponent(token)}`;
      const channel = parseChannelFromToken(token) || 'emb_channel';
      const snippetText = [
        `<iframe id="devspott-app-${toolId}" src="${iframeSrc}" style="width:100%;height:700px;border:0;" sandbox="allow-scripts allow-forms"></iframe>`,
        `<script src="${apiBase}/embed/devspott-embed.js"></script>`,
        '<script>',
        `  const iframe = document.getElementById('devspott-app-${toolId}');`,
        `  const client = window.DevspottEmbed.createClient({ iframe, channel: '${channel}', targetOrigin: '${apiBase}' });`,
        "  // client.refresh();",
        '</script>',
      ].join('\n');
      setSnippet(snippetText);
      toolToast.success(t('tools.embedPanel.snippetReady'));
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: string }).message || '')
          : '';
      toolToast.error(message || t('tools.embedPanel.tokenError'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopySnippet = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(snippet);
      toolToast.success(t('tools.embedPanel.copySuccess'));
    } catch {
      toolToast.error(t('tools.embedPanel.copyError'));
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label={t('tools.embedPanel.keyLabel')}
          value={label}
          onChange={event => setLabel(event.target.value)}
          placeholder={t('tools.embedPanel.keyLabelPlaceholder')}
        />
        <Input
          label={t('tools.embedPanel.maxRequestsPerHour')}
          value={maxRequestsPerHour}
          onChange={event => setMaxRequestsPerHour(event.target.value)}
          placeholder="1000"
          type="number"
          min={1}
        />
        <Input
          label={t('tools.embedPanel.maxBandwidthPerDay')}
          value={maxBandwidthMbPerDay}
          onChange={event => setMaxBandwidthMbPerDay(event.target.value)}
          placeholder="2048"
          type="number"
          min={1}
        />
        <div className="flex items-end pb-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={allowSameOrigin}
              onChange={event => setAllowSameOrigin(event.target.checked)}
            />
            {t('tools.embedPanel.allowSameOrigin')}
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={handleCreateKey} loading={isSaving} disabled={isSaving}>
          {t('tools.embedPanel.createKey')}
        </Button>
        <Button variant="outline" onClick={() => void loadKeys()} disabled={isLoading}>
          {t('common.refresh')}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {t('tools.embedPanel.existingKeys')}
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('common.loading')}
          </p>
        ) : activeKeyOptions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('tools.embedPanel.noKeys')}
          </p>
        ) : (
          <div className="space-y-2">
            {activeKeyOptions.map(item => (
              <label
                key={item.id}
                className="flex items-center justify-between rounded border border-gray-200 dark:border-gray-700 px-3 py-2 cursor-pointer"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {item.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {item.key_id}
                  </p>
                </div>
                <input
                  type="radio"
                  name="embed-key-id"
                  value={item.key_id}
                  checked={selectedKeyId === item.key_id}
                  onChange={() => setSelectedKeyId(item.key_id)}
                />
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label={t('tools.embedPanel.parentOrigin')}
          value={parentOrigin}
          onChange={event => setParentOrigin(event.target.value)}
          placeholder="https://app.exemplo.com"
        />
        <Input
          label={t('tools.embedPanel.expiresIn')}
          value={expiresInSeconds}
          onChange={event => setExpiresInSeconds(event.target.value)}
          type="number"
          min={60}
          max={86400}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={handleGenerateSnippet}
          loading={isGenerating}
          disabled={isGenerating || !selectedKeyId}
        >
          {t('tools.embedPanel.snippet')}
        </Button>
        <Button variant="outline" onClick={handleCopySnippet} disabled={!snippet}>
          {t('tools.embedPanel.copySnippet')}
        </Button>
      </div>
      {snippet && (
        <pre className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-xs overflow-auto whitespace-pre-wrap">
          {snippet}
        </pre>
      )}
    </div>
  );
};
